import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Tab,
  Tabs,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Assignment as TaskIcon,
  ExitToApp as LogoutIcon,
  Home as HomeIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { PortalCompanyInfo } from './components/PortalCompanyInfo';
import { PortalServiceRequests } from './components/PortalServiceRequests';
import { PortalBilling } from './components/PortalBilling';

interface PortalData {
  customer: any;
  tenantId: string;
  token: string;
  branding?: {
    primaryColor: string;
    secondaryColor: string;
    logoUrl?: string;
    companyName: string;
  };
  categories?: Array<{
    id: string;
    name: string;
    color: string;
    description?: string;
  }>;
  portalSettings?: {
    defaultWorkflowId?: string;
    defaultStageId?: string;
    defaultCategoryId?: string;
  };
  portalMessages?: {
    welcomeMessage: string;
    confirmationMessage: string;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`portal-tabpanel-${index}`}
      aria-labelledby={`portal-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 1, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

export const CustomerPortal: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [portalData, setPortalData] = useState<PortalData | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (token) {
      validateAndLoadPortal();
    }
  }, [token]);

  const validateAndLoadPortal = async () => {
    try {
      setLoading(true);
      
      // Get API URL from environment
      const API_URL = import.meta.env.VITE_SIMPLE_REST_URL || 'http://localhost:3000';
      
      // Validate token and get customer data
      const response = await fetch(`${API_URL}/portal/auth/${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Invalid or expired portal link');
      }
      
      const data = await response.json();
      setPortalData(data);
      
      // Store token for API calls
      sessionStorage.setItem('portalToken', token);
      
    } catch (err) {
      console.error('Portal validation error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load portal');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('portalToken');
    navigate('/');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        bgcolor: 'background.default'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error">
          <Typography variant="h6">Portal Access Error</Typography>
          <Typography>{error}</Typography>
        </Alert>
      </Container>
    );
  }

  if (!portalData) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="warning">
          <Typography>No portal data available</Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: 'grey.50',
      pb: 2,
    }}>
      {/* Header */}
      <AppBar 
        position="sticky" 
        elevation={1}
        sx={{ 
          bgcolor: portalData.branding?.primaryColor || 'primary.main',
        }}
      >
        <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
          {portalData.branding?.logoUrl ? (
            <Box 
              component="img" 
              src={portalData.branding.logoUrl} 
              alt={portalData.branding.companyName}
              sx={{ 
                height: isMobile ? 32 : 40, 
                mr: 2,
                objectFit: 'contain',
              }}
            />
          ) : (
            <BusinessIcon sx={{ mr: 2, fontSize: isMobile ? 24 : 28 }} />
          )}
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontSize: isMobile ? '0.95rem' : '1.25rem',
              fontWeight: 500,
            }}
          >
            {portalData.customer.company || portalData.branding?.companyName || 'Customer Portal'}
          </Typography>
          <IconButton 
            color="inherit" 
            onClick={handleLogout}
            size={isMobile ? 'small' : 'medium'}
            sx={{ p: isMobile ? 0.5 : 1 }}
          >
            <LogoutIcon fontSize={isMobile ? 'small' : 'medium'} />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Portal Content */}
      <Container maxWidth="md" sx={{ mt: { xs: 1, sm: 2 }, px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Navigation Tabs */}
        <Paper 
          sx={{ 
            mb: { xs: 2, sm: 3 },
            borderRadius: { xs: 1, sm: 2 },
          }}
        >
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTab-root': {
                minHeight: isMobile ? 48 : 56,
                fontSize: isMobile ? '0.7rem' : '0.875rem',
                minWidth: isMobile ? 80 : 120,
                px: isMobile ? 1 : 2,
              },
              '& .MuiTabs-indicator': {
                bgcolor: portalData.branding?.primaryColor || 'primary.main',
              },
            }}
          >
            <Tab 
              icon={<HomeIcon fontSize={isMobile ? 'small' : 'medium'} />} 
              label="Overview" 
              iconPosition="top"
              sx={{ 
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: portalData.branding?.primaryColor || 'primary.main',
                },
              }}
            />
            <Tab 
              icon={<TaskIcon fontSize={isMobile ? 'small' : 'medium'} />} 
              label="Service Requests" 
              iconPosition="top"
              sx={{ 
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: portalData.branding?.primaryColor || 'primary.main',
                },
              }}
            />
            <Tab 
              icon={<MoneyIcon fontSize={isMobile ? 'small' : 'medium'} />} 
              label="Billing" 
              iconPosition="top"
              sx={{ 
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: portalData.branding?.primaryColor || 'primary.main',
                },
              }}
            />
          </Tabs>
        </Paper>

        {/* Tab Panels */}
        <TabPanel value={activeTab} index={0}>
          <PortalCompanyInfo 
            customer={portalData.customer} 
            welcomeMessage={portalData.portalMessages?.welcomeMessage}
          />
        </TabPanel>
        
        <TabPanel value={activeTab} index={1}>
          <PortalServiceRequests 
            customerId={portalData.customer.id}
            tenantId={portalData.tenantId}
            customer={portalData.customer}
            categories={portalData.categories}
            portalSettings={portalData.portalSettings}
            confirmationMessage={portalData.portalMessages?.confirmationMessage}
          />
        </TabPanel>
        
        <TabPanel value={activeTab} index={2}>
          <PortalBilling
            customerId={portalData.customer.id}
            tenantId={portalData.tenantId}
          />
        </TabPanel>
      </Container>
    </Box>
  );
};